import { useNavigate } from '@tanstack/react-router'
import CantileverButton from 'components/CantileverButton'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import {
  MD_CASE_SEVERITIES,
  type MdCaseSeverity,
} from 'features/mdCases/mdCase'
import { buildMdCaseEntryFromDraftAlerts } from 'features/mdCases/mdDraftAlerts'
import { useGeckoNotifications } from 'features/notifications'
import { useGeckoPermissions } from 'features/permissions'
import {
  generateEntrySlug,
  useGetRolodexEntriesByLoadOptions,
} from 'features/rolodex'
import { useScatterplotContext } from 'features/timeSeries'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { BellPlus, X } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { SEARCH_PARAM_SITE_ID } from 'routes/(global_search_params)/-searchParams'
import { SEARCH_PARAM_CASE_ID } from 'routes/cases/-searchParams'
import { GeckoAlert, RolodexEntryType } from 'types'

import { MdDraftAlertCaseFlowPanels } from './MdDraftAlertCaseFlowPanels'
import type {
  DraftAlertDisplay,
  DraftAlertFormState,
  MdDraftAlertCaseFlowProps,
  OverlayAlert,
  PositionedOverlayAlert,
} from './mdDraftAlertCaseFlowTypes'
import {
  buildCaseAlertRemovalPatch,
  buildManualAlertEntry,
  normalizeAlertDatapointIDs,
  parseManualAlerts,
  selectManualAlertsForSensors,
} from './mdDraftAlertCaseFlowUtils'
import { useDraftAlertCapture } from './useDraftAlertCapture'
import { useManualAlertOverlay } from './useManualAlertOverlay'

const EMPTY_POSITIONED_ALERTS: readonly PositionedOverlayAlert[] = []

const getInitialFormState = (
  selectedSensorIds: readonly string[],
  preferredPrimarySensorId?: string
): DraftAlertFormState => {
  const primarySensorId =
    selectedSensorIds.find(
      (sensorId) => sensorId === preferredPrimarySensorId
    ) ??
    selectedSensorIds[0] ??
    ''

  return {
    primarySensorId,
    sensorIds: primarySensorId ? [primarySensorId] : [],
    title: '',
    severity: '',
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const mergeAlertsById = (
  ...alertGroups: readonly GeckoAlert[][]
): GeckoAlert[] => [
  ...new Map(
    alertGroups.flatMap((alerts) => alerts.map((alert) => [alert.id, alert]))
  ).values(),
]

function toOverlayAlert(alert: GeckoAlert, kind: 'saved'): OverlayAlert
function toOverlayAlert(alert: DraftAlertDisplay, kind: 'draft'): OverlayAlert
function toOverlayAlert(
  alert: GeckoAlert | DraftAlertDisplay,
  kind: 'saved' | 'draft'
): OverlayAlert {
  if (kind === 'saved') {
    const savedAlert = alert as GeckoAlert
    return {
      id: savedAlert.id,
      kind,
      title: savedAlert.data.title ?? null,
      summary: savedAlert.data.summary ?? '',
      severity: MD_CASE_SEVERITIES.includes(
        savedAlert.data.severity as MdCaseSeverity
      )
        ? (savedAlert.data.severity as MdCaseSeverity)
        : null,
      primarySensorId:
        savedAlert.data.primary_datapoint_id ??
        savedAlert.data.datapoint_ids?.[0] ??
        '',
      sensorIds: savedAlert.data.datapoint_ids ?? [],
      renderStartAt: savedAlert.data.render_start_at ?? '',
      renderEndAt: savedAlert.data.render_end_at ?? '',
      focusStartAt: savedAlert.data.focus_start_at ?? '',
      focusEndAt: savedAlert.data.focus_end_at ?? '',
      caseId: savedAlert.data.case_id ?? null,
    }
  }

  const draftAlert = alert as DraftAlertDisplay
  return {
    id: draftAlert.id,
    kind,
    title: draftAlert.title || null,
    summary: draftAlert.summary,
    severity: draftAlert.severity || null,
    primarySensorId: draftAlert.primarySensorId,
    sensorIds: draftAlert.sensorIds,
    renderStartAt: draftAlert.renderStartAt,
    renderEndAt: draftAlert.renderEndAt,
    focusStartAt: draftAlert.focusStartAt,
    focusEndAt: draftAlert.focusEndAt,
    caseId: null,
  }
}

export const MdDraftAlertCaseFlow: React.FC<MdDraftAlertCaseFlowProps> = ({
  uPlotRef,
  chartAreaRef,
  hasDataPoints,
  initialManualAlertId,
  allowSavedAlertDelete = true,
  disabled = false,
  onCapturePhaseChange,
}) => {
  const navigate = useNavigate()
  const { rolodexPut, rolodexPatch, rolodexDelete } = useRolodexEdit()
  const { errorNotification } = useGeckoNotifications()
  const { user: currentUser } = useGeckoPermissions()
  const {
    siteID,
    organizationID,
    chartID,
    transient,
    timeRange,
    selectedSensors,
    sensorAxisMapping,
    sensorVisibilityConfig,
    chartData,
    setHighlightedSensorId,
    setSecondaryHighlightedSensorIds,
    xAxisSensorId,
    singleScaleMode,
    isLoading,
    isInitializing,
    hideAllAlerts,
  } = useScatterplotContext()
  // In transient/ad-hoc mode the chartID is a locally generated slug with no
  // persisted Chart entry, so it must not be written onto new alerts or case
  // references — the pointer would dangle forever.
  const persistedChartID = transient ? null : chartID
  const { data: savedAlertEntries = [], mutate: refreshSavedAlerts } =
    useGetRolodexEntriesByLoadOptions<unknown[]>(
      siteID
        ? {
            type: [RolodexEntryType.ALERT],
            tags: { site: [siteID] },
            limit: 1000,
          }
        : {
            type: [],
            limit: 0,
          }
    )
  const { data: selectedAlertEntries = [] } = useGetRolodexEntriesByLoadOptions<
    unknown[]
  >(
    initialManualAlertId
      ? {
          ids: [initialManualAlertId],
          type: [RolodexEntryType.ALERT],
          limit: 1,
        }
      : {
          type: [],
          limit: 0,
        }
  )
  const savedManualAlerts = useMemo(
    () =>
      mergeAlertsById(
        parseManualAlerts(savedAlertEntries),
        parseManualAlerts(selectedAlertEntries)
      ),
    [savedAlertEntries, selectedAlertEntries]
  )

  const [isComposerOpen, setIsComposerOpen] = useState(false)
  const [isTrayVisible, setIsTrayVisible] = useState(true)
  const [isCreatingCase, setIsCreatingCase] = useState(false)
  const [formState, setFormState] = useState<DraftAlertFormState>(() =>
    getInitialFormState(selectedSensors.map((sensor) => sensor.id))
  )
  const [draftAlerts, setDraftAlerts] = useState<DraftAlertDisplay[]>([])
  const [isDeletingAlertId, setIsDeletingAlertId] = useState<string | null>(
    null
  )
  const [dismissedAlertIds, setDismissedAlertIds] = useState<Set<string>>(
    () => new Set()
  )

  const visibleSensors = useMemo(() => {
    const visible = selectedSensors.filter(
      (sensor) => sensorVisibilityConfig[sensor.id] !== false
    )
    return visible.length > 0 ? visible : selectedSensors
  }, [selectedSensors, sensorVisibilityConfig])

  const visibleSensorIds = useMemo(
    () => visibleSensors.map((sensor) => sensor.id),
    [visibleSensors]
  )

  const sensorsById = useMemo(
    () => new Map(selectedSensors.map((sensor) => [sensor.id, sensor])),
    [selectedSensors]
  )
  const selectedSensorIds = useMemo(
    () => selectedSensors.map((sensor) => sensor.id),
    [selectedSensors]
  )

  const canDraftAlerts =
    !xAxisSensorId &&
    hasDataPoints &&
    selectedSensors.length > 0 &&
    !!chartID &&
    !!siteID &&
    !!organizationID

  const captureDisabledReason = xAxisSensorId
    ? 'Manual alerts require time on the x-axis'
    : !selectedSensors.length
      ? 'Select a sensor first'
      : !hasDataPoints
        ? 'Load data in the selected time range first'
        : 'Draft Manual Alert'

  const chartDataExtentKey = useMemo(() => {
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    let count = 0

    chartData.forEach((point) => {
      const timestamp = point.timestamp
      if (typeof timestamp !== 'number' || Number.isNaN(timestamp)) {
        return
      }

      count += 1
      min = Math.min(min, timestamp)
      max = Math.max(max, timestamp)
    })

    return count > 0 ? `${count}:${min}:${max}` : 'empty'
  }, [chartData])

  const sensorMatchedSavedAlerts = useMemo(
    () =>
      selectManualAlertsForSensors({
        alerts: savedManualAlerts,
        sensorIDs: selectedSensorIds,
      }),
    [savedManualAlerts, selectedSensorIds]
  )

  const sensorMatchedDraftAlerts = useMemo(() => {
    if (selectedSensorIds.length === 0) {
      return []
    }

    const sensorIDSet = new Set(selectedSensorIds)

    return draftAlerts.filter((alert) =>
      alert.sensorIds.some((sensorId) => sensorIDSet.has(sensorId))
    )
  }, [draftAlerts, selectedSensorIds])

  const overlayAlerts = useMemo(
    () => [
      ...sensorMatchedSavedAlerts.map((alert) =>
        toOverlayAlert(alert, 'saved')
      ),
      ...sensorMatchedDraftAlerts.map((alert) =>
        toOverlayAlert(alert, 'draft')
      ),
    ],
    [sensorMatchedSavedAlerts, sensorMatchedDraftAlerts]
  )

  const linkedCaseIds = useMemo(
    () => [
      ...new Set(
        savedManualAlerts.flatMap((alert) =>
          alert.data.case_id ? [alert.data.case_id] : []
        )
      ),
    ],
    [savedManualAlerts]
  )

  const { data: linkedCasesData = [] } = useGetRolodexEntriesByLoadOptions<
    unknown[]
  >(
    linkedCaseIds.length > 0
      ? {
          ids: linkedCaseIds,
          type: [RolodexEntryType.CASE],
          limit: linkedCaseIds.length,
        }
      : {
          type: [],
          limit: 0,
        }
  )

  const linkedCasesById = useMemo(
    () =>
      new Map<string, Record<string, unknown>>(
        linkedCasesData.flatMap((entry) =>
          isRecord(entry) && typeof entry.id === 'string'
            ? [[entry.id, entry]]
            : []
        )
      ),
    [linkedCasesData]
  )

  const {
    capturePhase,
    pendingCapture,
    capturePrimarySensorId,
    draftSelectionPreview,
    startCapture: beginCapture,
    cancelCapture,
    resetCapture,
    handleCaptureMouseDown,
    handleCaptureMouseMove,
    handleCaptureMouseUp,
  } = useDraftAlertCapture({
    uPlotRef,
    chartAreaRef,
    visibleSensors,
    visibleSensorIds,
    chartData,
    sensorAxisMapping,
    singleScaleMode,
    timeRange,
    canDraftAlerts,
    onCaptureComplete: () => setIsComposerOpen(true),
  })

  useEffect(() => {
    onCapturePhaseChange?.(capturePhase !== 'idle')
  }, [capturePhase, onCapturePhaseChange])

  useEffect(() => {
    if (!pendingCapture) {
      return
    }

    setFormState(
      getInitialFormState(visibleSensorIds, pendingCapture.primarySensorId)
    )
  }, [pendingCapture, visibleSensorIds])

  useEffect(() => {
    setFormState((currentState) => {
      const nextPrimarySensorId =
        visibleSensorIds.find(
          (sensorId) => sensorId === currentState.primarySensorId
        ) ??
        visibleSensorIds[0] ??
        ''
      const filteredSensorIds = normalizeAlertDatapointIDs(
        nextPrimarySensorId,
        currentState.sensorIds.filter((sensorId) =>
          visibleSensorIds.includes(sensorId)
        )
      )

      if (
        nextPrimarySensorId === currentState.primarySensorId &&
        filteredSensorIds.length === currentState.sensorIds.length &&
        filteredSensorIds.every(
          (sensorId, index) => sensorId === currentState.sensorIds[index]
        )
      ) {
        return currentState
      }

      return {
        ...currentState,
        primarySensorId: nextPrimarySensorId,
        sensorIds: filteredSensorIds,
      }
    })
  }, [visibleSensorIds])

  const {
    positionedAlerts,
    activePositionedAlert,
    startHoverAlert,
    scheduleHoverAlertHide,
    clearHoverAlert,
  } = useManualAlertOverlay({
    overlayAlerts,
    uPlotRef,
    chartAreaRef,
    xAxisSensorId,
    initialManualAlertId,
    capturePrimarySensorId,
    chartDataLength: chartData.length,
    chartDataExtentKey,
    selectedSensorsLength: selectedSensors.length,
    isChartLoading: isLoading || isInitializing,
    setHighlightedSensorId,
    setSecondaryHighlightedSensorIds,
  })

  const startCapture = useCallback(() => {
    clearHoverAlert()
    beginCapture()
  }, [beginCapture, clearHoverAlert])

  const handleToggleAlert = useCallback(
    (alertId: string) => {
      clearHoverAlert()
      setDismissedAlertIds((current) => {
        const next = new Set(current)
        if (next.has(alertId)) {
          next.delete(alertId)
        } else {
          next.add(alertId)
        }
        return next
      })
    },
    [clearHoverAlert]
  )

  const resetComposer = useCallback(() => {
    resetCapture()
    setIsComposerOpen(false)
    setFormState(getInitialFormState(visibleSensorIds))
  }, [resetCapture, visibleSensorIds])

  const handleDraftAlert = () => {
    if (!pendingCapture) {
      return
    }

    const title = formState.title.trim()
    const normalizedSensorIds = normalizeAlertDatapointIDs(
      formState.primarySensorId,
      formState.sensorIds
    )

    if (
      !title ||
      !formState.primarySensorId ||
      normalizedSensorIds.length === 0
    ) {
      return
    }

    setDraftAlerts((currentAlerts) => [
      ...currentAlerts,
      {
        id: generateEntrySlug(`manual-alert-${Date.now()}`),
        primarySensorId: formState.primarySensorId,
        sensorIds: normalizedSensorIds,
        title,
        summary: title,
        severity: formState.severity,
        renderStartAt: pendingCapture.renderStartAt,
        renderEndAt: pendingCapture.renderEndAt,
        focusStartAt: pendingCapture.focusStartAt,
        focusEndAt: pendingCapture.focusEndAt,
      },
    ])
    setIsTrayVisible(true)
    resetComposer()
  }

  const handleRemoveDraftAlert = (alertId: string) => {
    setDraftAlerts((currentAlerts) =>
      currentAlerts.filter((alert) => alert.id !== alertId)
    )
  }

  const handleCreateCase = async () => {
    if (!draftAlerts.length || isCreatingCase) {
      return
    }

    setIsCreatingCase(true)

    try {
      const caseID = generateEntrySlug(`case-${Date.now()}`)
      const createdAt = new Date().toISOString()
      const manualAlertEntries = draftAlerts.map((alert) =>
        buildManualAlertEntry({
          id: alert.id,
          title: alert.title || null,
          summary: alert.summary,
          severity: alert.severity || null,
          chartID: persistedChartID,
          siteID,
          organizationID,
          primaryDatapointID: alert.primarySensorId,
          datapointIDs: alert.sensorIds,
          renderStartAt: alert.renderStartAt,
          renderEndAt: alert.renderEndAt,
          focusStartAt: alert.focusStartAt,
          focusEndAt: alert.focusEndAt,
          caseID,
          createdAt,
          createdBy: currentUser?.email ?? null,
        })
      )

      await rolodexPut([
        ...manualAlertEntries,
        buildMdCaseEntryFromDraftAlerts({
          id: caseID,
          siteID,
          organizationID,
          draftAlerts: draftAlerts.map((alert) => ({
            id: alert.id,
            pi_tag_id: alert.primarySensorId,
            pi_tag_ids: alert.sensorIds,
            start_at: alert.renderStartAt,
            end_at: alert.renderEndAt,
            focus_start_at: alert.focusStartAt,
            focus_end_at: alert.focusEndAt,
            summary: alert.summary,
            title: alert.title || undefined,
            severity: alert.severity || undefined,
            chart_id: persistedChartID,
            added_by: currentUser?.email ?? null,
            added_at: createdAt,
          })),
        }),
      ])

      setDraftAlerts([])
      setIsTrayVisible(true)
      navigate({
        to: '/cases/details',
        search: {
          [SEARCH_PARAM_SITE_ID]: siteID,
          [SEARCH_PARAM_CASE_ID]: caseID,
        },
      })
    } catch (error) {
      errorNotification(`Failed to create case: ${String(error)}`)
    } finally {
      setIsCreatingCase(false)
    }
  }

  const handleOpenCase = (caseId: string | null) => {
    if (!caseId) {
      return
    }

    navigate({
      to: '/cases/details',
      search: {
        [SEARCH_PARAM_SITE_ID]: siteID,
        [SEARCH_PARAM_CASE_ID]: caseId,
      },
    })
  }

  const handleDeleteAlert = async (alert: OverlayAlert) => {
    if (alert.kind !== 'saved' || isDeletingAlertId === alert.id) {
      return
    }

    const confirmationMessage = alert.caseId
      ? 'Delete this alert? It will also be removed from the linked M&D case.'
      : 'Delete this alert?'

    if (!window.confirm(confirmationMessage)) {
      return
    }

    setIsDeletingAlertId(alert.id)

    try {
      const casePatch =
        alert.caseId && linkedCasesById.has(alert.caseId)
          ? buildCaseAlertRemovalPatch(
              linkedCasesById.get(alert.caseId)!,
              alert.id
            )
          : null

      if (casePatch) {
        await rolodexPatch([casePatch])
      }

      await rolodexDelete(alert.id, false)
      clearHoverAlert()
      await refreshSavedAlerts()
    } catch (error) {
      errorNotification(`Failed to delete alert: ${String(error)}`)
    } finally {
      setIsDeletingAlertId((current) => (current === alert.id ? null : current))
    }
  }

  const draftAlertButtonLabel =
    capturePhase === 'idle'
      ? disabled
        ? 'Manual alerts are unavailable while another chart tool is active'
        : captureDisabledReason
      : 'Cancel manual alert capture'

  return (
    <>
      <ChartTooltip label={draftAlertButtonLabel} side='top'>
        <CantileverButton
          iconOnly
          ghost
          size='small'
          aria-label={draftAlertButtonLabel}
          disabled={capturePhase === 'idle' && (!canDraftAlerts || disabled)}
          onClick={capturePhase === 'idle' ? startCapture : cancelCapture}>
          {capturePhase === 'idle' ? <BellPlus size={16} /> : <X size={16} />}
        </CantileverButton>
      </ChartTooltip>

      <MdDraftAlertCaseFlowPanels
        chartAreaRef={chartAreaRef}
        visibleSensors={visibleSensors}
        formState={formState}
        setFormState={setFormState}
        pendingCapture={pendingCapture}
        isComposerOpen={isComposerOpen}
        onCancelComposer={resetComposer}
        onSubmitComposer={handleDraftAlert}
        positionedAlerts={
          hideAllAlerts ? EMPTY_POSITIONED_ALERTS : positionedAlerts
        }
        activePositionedAlert={hideAllAlerts ? null : activePositionedAlert}
        capturePhase={capturePhase}
        draftSelectionPreview={draftSelectionPreview}
        sensorsById={sensorsById}
        isDeletingAlertId={isDeletingAlertId}
        allowSavedAlertDelete={allowSavedAlertDelete}
        onDeleteAlert={handleDeleteAlert}
        onOpenCase={handleOpenCase}
        onStartHoverAlert={startHoverAlert}
        onScheduleHoverAlertHide={scheduleHoverAlertHide}
        onToggleAlert={handleToggleAlert}
        dismissedAlertIds={dismissedAlertIds}
        captureHandlers={{
          onMouseDown: handleCaptureMouseDown,
          onMouseMove: handleCaptureMouseMove,
          onMouseUp: handleCaptureMouseUp,
        }}
        draftAlerts={draftAlerts}
        isTrayVisible={isTrayVisible}
        setIsTrayVisible={setIsTrayVisible}
        isCreatingCase={isCreatingCase}
        onCreateCase={handleCreateCase}
        onRemoveDraftAlert={handleRemoveDraftAlert}
      />
    </>
  )
}
